import { useState, useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  Bot,
  Check,
  FileText,
  Link2,
  Loader2,
  MessagesSquare,
  RefreshCw,
  Search,
  Sparkles,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useApi } from '@/hooks/use-api'
import { useLimitsPlan } from '@/hooks/use-plan'
import { useLeads, leadKeys } from '@/features/leads/use-leads'
import { SequenceBuilder } from './sequence-builder'
import { useCreateCampaign, useActivateCampaign } from './use-campaigns'
import {
  clearWizardDraft,
  clearWizardReturn,
  flagWizardReturn,
  loadWizardDraft,
  saveWizardDraft,
  takePreselectLeadIds,
} from './wizard-draft'
import {
  ResourcesEditor,
  SettingsLinkSuggestion,
  cleanResourceRows,
  type ResourceRow,
} from './campaign-resources'
import type { CreateCampaignInput, SequenceConfig, SequenceStep } from './types'
import {
  maxLeadsPerCampaignFor,
  campaignCapMessage,
  CTA_TYPES,
  CTA_TYPE_UI_LABELS,
  type CtaType,
  type FaqConfig,
  type ObjectionConfig,
  type OfferConfig,
  emptyCampaignBrief,
  missingBriefFields,
  normalizeCampaignBrief,
  renderCampaignBrief,
  type CampaignBrief,
} from '@dealcloser/shared'
import { CampaignBriefEditor, briefFromOffer, type BriefSources } from './brief/brief-editor'
import { BriefSummary } from './brief/brief-summary'
import { PreviewVerdictBadge, type PreviewVerdict } from './preview-verdict-badge'
import { TemplateEditor } from './templates/template-editor'
import {
  isTemplateStep,
  messageWriterPayload,
  slotValuesWithLink,
  syncAngle,
  type TemplateChoice,
  type WizardMessageWriter,
} from './templates/template-fill'
import {
  STEP,
  TOTAL_STEPS,
  URL_PATTERN,
  WIZARD_STEPS,
  campaignLinkFor,
  canCreate,
  stepReady,
} from './wizard-steps'
import {
  CLAUDE_SETTINGS_PATH,
  DEFAULT_FALLBACK_HOURS,
  FALLBACK_OPTIONS,
  MESSAGE_WRITER_COPY,
  MESSAGE_WRITER_ORDER,
  hasWriteConnection,
  routinePrompt,
} from '@/features/settings/mcp-helpers'
import { useMcpConnections } from '@/features/settings/use-mcp'
import { CopyButton } from '@/features/settings/claude-settings'
import { cn } from '@/lib/utils'

/** Settings > Offer, where the account's offer, FAQ and objections live. */
const OFFER_SETTINGS_PATH = '/settings?tab=outreach'

const WRITER_ICONS: Record<WizardMessageWriter, typeof Bot> = { dca: Sparkles, template: FileText, mcp: Bot }

/** Helper copy under the link input, per call to action. */
const CTA_LINK_HINTS: Record<CtaType, string> = {
  book_demo: 'Your demo booking page. Pasted into the reply when a lead agrees to see a demo.',
  book_discovery_call: 'Your discovery call booking page. Pasted when a lead agrees to a call.',
  book_strategy_call: 'Your strategy call booking page. Pasted when a lead agrees to a call.',
  free_audit: 'Where a lead requests the audit. Sent as soon as a lead shows interest.',
  send_to_page: 'The landing page. Sent as soon as a lead shows interest.',
  send_to_trial: 'Your free trial signup page. Sent as soon as a lead shows interest.',
  send_to_event: 'The event registration page. Sent as soon as a lead shows interest.',
  direct_conversation: 'No link is sent. The AI keeps the conversation going in LinkedIn.',
  calendar_link: 'Your calendar link. Pasted when a lead agrees to grab a time.',
}

type IcpConfig = {
  readonly jobTitles: readonly string[]
  readonly industries: readonly string[]
  readonly companySizes: readonly string[]
  readonly locations: readonly string[]
  readonly industryKeywords: string
  readonly competitorUrls: readonly string[]
}

const STEP_TYPE_LABELS: Record<string, string> = {
  connection_request: 'Connection Request',
  first_dm: 'First DM',
  follow_up_1: 'Follow-up 1',
  follow_up_2: 'Follow-up 2',
  follow_up_3: 'Follow-up 3',
  follow_up_4: 'Follow-up 4',
  follow_up_5: 'Follow-up 5',
  follow_up_6: 'Follow-up 6',
  follow_up_7: 'Follow-up 7',
}

// Full 5-step cadence by default. Previously this was 3 steps (capped at
// follow_up_1), which meant getNextStepFromSequence() returned 'none' for
// any lead that reached followup_1_sent — so follow-ups 2 and 3 never fired
// and the kanban columns stayed empty. Matches the Pro-plan default in
// v3/workers/campaigns/src/routes/sequence.ts.
//
// strategy: 'auto' on every step (owner directive 2026-08-31): users are not
// asked to pick message strategies; the workers resolve 'auto' to a concrete
// angle per lead. Power users can pin one under Advanced in the builder.
const DEFAULT_SEQUENCE_STEPS: readonly SequenceStep[] = [
  { type: 'connection_request', delay_days: 0, strategy: 'auto' },
  { type: 'first_dm', delay_days: 1, strategy: 'auto' },
  { type: 'follow_up_1', delay_days: 2, strategy: 'auto' },
  { type: 'follow_up_2', delay_days: 4, strategy: 'auto' },
  { type: 'follow_up_3', delay_days: 7, strategy: 'auto' },
]

const DEFAULT_SEQUENCE_CONFIG: SequenceConfig = {
  steps: DEFAULT_SEQUENCE_STEPS,
  daily_limit: 30,
  activity_window: { start_hour: 9, end_hour: 17, timezone: 'America/New_York' },
  ramp_up: true,
}

type WizardState = {
  name: string
  /** What the AI asks leads in this campaign to do. */
  cta_type: CtaType
  /** The link the AI sends for that call to action (empty only for direct_conversation). */
  offer_link: string
  daily_limit: number
  lead_ids: readonly string[]
  sequence_steps: readonly SequenceStep[]
  target_persona: string
  /**
   * Additional links & attachments rows (settings.resources, max 5). Editing
   * shape — blank rows tolerated; pruned/trimmed at create time. Drafts saved
   * before this field existed simply restore without it (merge over
   * INITIAL_STATE keeps the empty default).
   *
   * Writing samples live on each step (`style_samples`) and the campaign
   * saves style_samples_mode 'per_step': "One set for all steps" was retired
   * 2026-10-28 (plan N4). wizard-draft.ts moves a v1 draft's global samples
   * onto the First DM.
   */
  resources: readonly ResourceRow[]
  /**
   * Messaging brief from the interview step (meeting 2026-09-18). The user
   * sees the exact prompt it renders to and must confirm it; any edit after
   * confirming un-confirms it so what is confirmed is always the final text.
   */
  brief: CampaignBrief
  brief_confirmed: boolean
  /** Whether to flip the new campaign from draft → active right after creation. */
  activate_now: boolean
  /**
   * Who writes the messages, picked on the Method step: DealCloser's AI
   * (default), a template the user edits and that is sent as written (card
   * S3), or the user's own Claude through the MCP connector. Template text
   * lives on each step (`template`); drafts saved before this field restore
   * as 'dca'.
   */
  message_writer: WizardMessageWriter
  /** The library sequence picked in template mode, or None (write your own). */
  template_angle: TemplateChoice | null
  /** Values typed for the sequence's [slots], filled in at save time. */
  template_slots: Readonly<Record<string, string>>
  /** Your Claude only: hours before DealCloser AI writes a due message itself (0 = never). */
  mcp_fallback_hours: number
}

const INITIAL_STATE: WizardState = {
  name: '',
  cta_type: 'book_demo',
  offer_link: '',
  daily_limit: 30,
  lead_ids: [],
  sequence_steps: DEFAULT_SEQUENCE_STEPS,
  target_persona: '',
  resources: [],
  brief: emptyCampaignBrief(),
  brief_confirmed: false,
  activate_now: false,
  message_writer: 'dca',
  template_angle: null,
  template_slots: {},
  mcp_fallback_hours: DEFAULT_FALLBACK_HOURS,
}

type StepIndicatorProps = {
  readonly currentStep: number
}

/**
 * Six numbered dots. Only the current step's name is spelled out: six
 * names do not fit the dialog's width, and the others keep a tooltip.
 */
function StepIndicator({ currentStep }: StepIndicatorProps) {
  const steps = WIZARD_STEPS.map((label) => ({ label }))

  return (
    <div aria-label={`Step ${currentStep} of ${TOTAL_STEPS}`}>
      <div className="flex items-center gap-0">
        {steps.map((step, index) => {
          const stepNumber = index + 1
          const isCompleted = stepNumber < currentStep
          const isCurrent = stepNumber === currentStep
  
          return (
            <div key={step.label} className="flex items-center" title={step.label}>
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
                  isCompleted
                    ? 'bg-primary text-primary-foreground'
                    : isCurrent
                      ? 'border-2 border-cobalt-bright text-cobalt-bright'
                      : 'border-2 border-border text-muted-foreground'
                }`}
              >
                {isCompleted ? <Check className="h-3.5 w-3.5" /> : stepNumber}
              </div>
              {isCurrent && <span className="ml-1.5 hidden whitespace-nowrap text-xs font-medium sm:inline">{step.label}</span>}
              {index < steps.length - 1 && (
                <div
                  className={`mx-1 h-px w-3 sm:mx-2 sm:w-4 ${stepNumber < currentStep ? 'bg-primary' : 'bg-border'}`}
                />
              )}
            </div>
          )
        })}
      </div>
      {/* Phones: the inline label does not fit beside six circles, so it sits below. */}
      <p className="mt-1.5 text-xs font-medium sm:hidden">{steps[currentStep - 1]?.label}</p>
    </div>
  )
}

type Step1Props = {
  readonly name: string
  readonly onChange: (name: string) => void
  readonly ctaType: CtaType
  readonly onCtaTypeChange: (type: CtaType) => void
  readonly offerLink: string
  readonly onOfferLinkChange: (link: string) => void
  /** The link saved under Settings, Offer — offered as a one-click prefill. */
  readonly settingsLink: string | undefined
  readonly resources: readonly ResourceRow[]
  readonly onResourcesChange: (rows: readonly ResourceRow[]) => void
  /** The account's single offer (Settings > Offer); undefined while loading. */
  readonly offer: OfferConfig | null | undefined
  /** Leave for Settings > Offer; the draft is kept and the return banner brings the user back. */
  readonly onEditOffer: () => void
}

/** The one-line name of the account's offer, or '' when none is saved. */
function offerOneLiner(offer: OfferConfig | null | undefined): string {
  return (offer?.coreOfferOneLiner || offer?.offerDescription || '').trim()
}

// 2026-07-27 owner directive: the per-campaign daily message limit is no
// longer user-configurable or shown anywhere. The platform sets daily sending
// volume from plan tier + LinkedIn safety rules (the wizard still submits the
// standard default so the backend cap machinery is unchanged).
//
// 2026-08-30 owner directive: the call to action and its link are set PER
// CAMPAIGN here (not in Settings, Offer). Both are required, except that
// "Direct Conversation" sends no link.
function Step1Details({
  name,
  onChange,
  ctaType,
  onCtaTypeChange,
  offerLink,
  onOfferLinkChange,
  settingsLink,
  resources,
  onResourcesChange,
  offer,
  onEditOffer,
}: Step1Props) {
  const needsLink = ctaType !== 'direct_conversation'
  const trimmedLink = offerLink.trim()
  const linkInvalid = needsLink && trimmedLink !== '' && !URL_PATTERN.test(trimmedLink)
  const offerLine = offerOneLiner(offer)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="campaign-name">Campaign Name</Label>
        <Input
          id="campaign-name"
          placeholder="e.g. Q1 SaaS Founders Outreach"
          value={name}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>

      {/* Offer (plan N6): every campaign uses the account's one offer from
          Settings > Offer, so it is shown here with a way to change it. */}
      <div className="flex flex-col gap-1 rounded-md border px-3 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium">Offer</p>
          <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs" onClick={onEditOffer}>
            Edit in Settings
          </Button>
        </div>
        {offer === undefined ? (
          <p className="text-xs text-muted-foreground">Loading your offer...</p>
        ) : offerLine ? (
          <p className="text-sm text-muted-foreground line-clamp-2">{offerLine}</p>
        ) : (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            No offer saved yet. Add one in Settings so the AI knows what you sell, or answer the questions in
            the What the AI knows step.
          </p>
        )}
        <p className="text-[11px] text-muted-foreground">
          From Settings, Offer, along with your FAQ and objections. The What the AI knows step starts from it.
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="campaign-cta-type">Goal</Label>
        <Select value={ctaType} onValueChange={(v) => onCtaTypeChange(v as CtaType)}>
          <SelectTrigger id="campaign-cta-type">
            <SelectValue placeholder="What should leads do?" />
          </SelectTrigger>
          <SelectContent>
            {CTA_TYPES.map((t) => (
              <SelectItem key={t} value={t}>{CTA_TYPE_UI_LABELS[t]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          What this campaign asks leads to do. Every close steers toward this.
        </p>
      </div>

      {needsLink && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="campaign-offer-link">Link the AI sends</Label>
          {/* One-click prefill from Settings, Offer while the field is empty;
              typing into the input ("or paste a different link") hides it. */}
          <SettingsLinkSuggestion
            settingsLink={settingsLink}
            currentLink={offerLink}
            onUse={onOfferLinkChange}
          />
          <Input
            id="campaign-offer-link"
            type="url"
            required
            placeholder="https://"
            value={offerLink}
            onChange={(e) => onOfferLinkChange(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            {CTA_LINK_HINTS[ctaType]} Pasted exactly as written. You can change
            it later from Edit Campaign.
          </p>
          {linkInvalid && (
            <p className="text-xs text-destructive">
              Enter a full URL starting with https://
            </p>
          )}
        </div>
      )}
      {!needsLink && (
        <p className="text-xs text-muted-foreground">{CTA_LINK_HINTS.direct_conversation}</p>
      )}

      <ResourcesEditor rows={resources} onChange={onResourcesChange} idPrefix="wizard" />

      <div className="rounded-md border bg-muted/30 px-3 py-2.5">
        <p className="text-sm font-medium">Sending schedule</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Messages go out between 9:00 AM and 5:00 PM New York time, Monday
          through Friday. Weekends are paused unless you switch on weekend
          activity in Settings, Safety. Daily sending volume is managed for
          you within LinkedIn's safe limits.
        </p>
      </div>
    </div>
  )
}

type Step2Props = {
  readonly selectedIds: readonly string[]
  readonly onChange: (ids: readonly string[]) => void
}

function Step2Leads({ selectedIds, onChange }: Step2Props) {
  const { data: leads = [], isLoading } = useLeads()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')

  // Escape hatch out of the campaign-first dead end (owner complaint,
  // 2026-08-31): a new account reaches this step with zero leads and used to
  // be told to finish the campaign and add leads "afterwards". Now they can
  // jump straight to Find Leads or the URL import. The wizard's draft is
  // already parked in sessionStorage by the persistence effect; the return
  // flag makes the destination page show a "Back to campaign setup" banner
  // that reopens the wizard exactly where they left off.
  function goAddLeads(path: string) {
    flagWizardReturn()
    navigate(path)
  }
  // Default to leads that are NOT already in a campaign, so "Select all"
  // can't quietly pull people out of a running sequence (beta QA feedback).
  // "All leads" stays available for deliberate moves and shows an amber
  // "In: <campaign>" badge on every assigned lead.
  const [scope, setScope] = useState<'available' | 'all'>('available')

  // Per-plan campaign cap (Basic 30 / Pro 60; comp + agency unlimited). A new
  // campaign starts empty, so the number of selectable leads == the cap. The
  // backend trims anything beyond this too — clamping here just keeps the
  // selection honest. Cap math uses the comp-aware limits plan: usePlan()
  // routes comp accounts to 'pro' for UI gating, but their server-side cap is
  // Agency-unlimited, so clamping at 60 here blocked beta testers for nothing.
  const plan = useLimitsPlan()
  const perCampaignCap = maxLeadsPerCampaignFor(plan)
  const capReached = Number.isFinite(perCampaignCap) && selectedIds.length >= perCampaignCap

  const availableLeads = leads.filter((lead) => !lead.campaign_id)
  const scoped = scope === 'available' ? availableLeads : leads

  const filtered = search.trim()
    ? scoped.filter((lead) => {
        const q = search.toLowerCase()
        const name = (lead.full_name ?? `${lead.first_name} ${lead.last_name}`).toLowerCase()
        const title = (lead.job_title ?? '').toLowerCase()
        const company = (lead.company ?? '').toLowerCase()
        return name.includes(q) || title.includes(q) || company.includes(q)
      })
    : scoped

  const selectedFromOtherCampaigns = leads.filter(
    (lead) => lead.campaign_id && selectedIds.includes(lead.id),
  ).length

  function toggleLead(id: string) {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((lid) => lid !== id))
    } else {
      // Respect the per-plan cap — ignore adds once we're full.
      if (Number.isFinite(perCampaignCap) && selectedIds.length >= perCampaignCap) return
      onChange([...selectedIds, id])
    }
  }

  function toggleAll(checked: boolean) {
    if (checked) {
      const allIds = filtered.map((l) => l.id)
      let merged = [...new Set([...selectedIds, ...allIds])]
      if (Number.isFinite(perCampaignCap) && merged.length > perCampaignCap) {
        merged = merged.slice(0, perCampaignCap)
      }
      onChange(merged)
    } else {
      const filteredIds = new Set(filtered.map((l) => l.id))
      onChange(selectedIds.filter((id) => !filteredIds.has(id)))
    }
  }

  const allFilteredSelected = filtered.length > 0 && filtered.every((l) => selectedIds.includes(l.id))

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-8 text-center">Loading leads...</div>
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Select leads to add to this campaign.
        </p>
        <div className="flex items-center gap-2">
          {selectedIds.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-muted-foreground"
              onClick={() => onChange([])}
            >
              Clear selection
            </Button>
          )}
          <Badge variant="secondary">
            {selectedIds.length}
            {Number.isFinite(perCampaignCap) ? ` / ${perCampaignCap}` : ''} selected
          </Badge>
        </div>
      </div>

      {/* Scope toggle: keep already-campaigned leads out of the picker by
          default so nobody gets double-sequenced by accident. The add-leads
          escape hatches sit on the right so they're reachable even when the
          list has leads (not only from the empty state). */}
      <div className="flex flex-wrap items-center gap-1">
        <Button
          variant={scope === 'available' ? 'secondary' : 'ghost'}
          size="sm"
          className="h-7 px-2.5 text-xs"
          onClick={() => setScope('available')}
        >
          Not in a campaign ({availableLeads.length})
        </Button>
        <Button
          variant={scope === 'all' ? 'secondary' : 'ghost'}
          size="sm"
          className="h-7 px-2.5 text-xs"
          onClick={() => setScope('all')}
        >
          All leads ({leads.length})
        </Button>
        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2.5 text-xs"
            onClick={() => goAddLeads('/leads/discover')}
          >
            <Search className="h-3 w-3" />
            Find new leads
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2.5 text-xs"
            onClick={() => goAddLeads('/leads?add=url')}
          >
            <Link2 className="h-3 w-3" />
            Import by URL
          </Button>
        </div>
      </div>

      {capReached && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          {campaignCapMessage(plan)}
        </p>
      )}

      {selectedFromOtherCampaigns > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>
            {selectedFromOtherCampaigns === 1
              ? '1 selected lead is already in another campaign and will be moved into this new one.'
              : `${selectedFromOtherCampaigns} selected leads are already in other campaigns and will be moved into this new one.`}
          </span>
        </div>
      )}

      {leads.length > 5 && (
        <Input
          placeholder="Search leads..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 text-sm"
        />
      )}

      <div className="max-h-72 overflow-y-auto rounded-md border divide-y">
        {leads.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <p className="text-sm text-muted-foreground px-4">
              No leads yet. Add some now. Your campaign setup is saved, and
              you can pick up right where you left off.
            </p>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => goAddLeads('/leads/discover')}>
                <Search className="h-3.5 w-3.5" />
                Find new leads
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => goAddLeads('/leads?add=url')}
              >
                <Link2 className="h-3.5 w-3.5" />
                Import by URL
              </Button>
            </div>
          </div>
        )}
        {leads.length > 0 && scoped.length === 0 && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            Every lead is already in a campaign. Switch to "All leads" to move
            leads out of their current campaign.
          </div>
        )}
        {filtered.length > 0 && (
          <div
            className="flex items-center gap-3 px-3 py-2 bg-muted/30 cursor-pointer sticky top-0 z-10 border-b"
            onClick={() => toggleAll(!allFilteredSelected)}
          >
            <Checkbox
              checked={allFilteredSelected}
              onCheckedChange={(checked) => toggleAll(!!checked)}
              aria-label="Select all leads"
            />
            <span className="text-sm font-medium text-muted-foreground">
              Select all{search.trim() ? ` (${filtered.length} matching)` : ` (${filtered.length})`}
            </span>
          </div>
        )}
        {filtered.map((lead) => {
          const displayName = lead.full_name ?? `${lead.first_name} ${lead.last_name}`
          const isSelected = selectedIds.includes(lead.id)

          return (
            <div
              key={lead.id}
              className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 cursor-pointer"
              onClick={() => toggleLead(lead.id)}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => toggleLead(lead.id)}
                aria-label={`Select ${displayName}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium truncate">{displayName}</p>
                  {lead.campaign_id && (
                    <Badge
                      variant="outline"
                      className="shrink-0 text-[10px] px-1.5 py-0 h-4 border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200"
                    >
                      In: {lead.campaign ?? 'another campaign'}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {lead.job_title ?? 'Unknown role'} &middot; {lead.company ?? 'Unknown company'}
                </p>
              </div>
            </div>
          )
        })}
        {search.trim() && filtered.length === 0 && scoped.length > 0 && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            No leads match "{search}"
          </div>
        )}
      </div>
    </div>
  )
}

type StepMethodProps = {
  readonly writer: WizardMessageWriter
  readonly onWriterChange: (writer: WizardMessageWriter) => void
  /** The user has a live Claude connection that may write. */
  readonly mcpAvailable: boolean
  /** Still checking the user's Claude connections. */
  readonly mcpLoading: boolean
  /** Leave for Settings > Claude; the draft is kept and the return banner brings the user back. */
  readonly onOpenClaudeSettings: () => void
}

/**
 * Step 3, Method (plan N2): who writes the messages. Before 2026-10-28 this
 * choice sat inside the Sequence step, and Your Claude could only be picked
 * on the campaign page after creating the campaign. The card copy is shared
 * with the campaign page's "Who writes the messages" card.
 */
function StepMethod({ writer, onWriterChange, mcpAvailable, mcpLoading, onOpenClaudeSettings }: StepMethodProps) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">
        Who writes this campaign&rsquo;s messages? Whichever you pick, DealCloser decides who is due and when, and
        keeps your daily limits and sending hours.
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Who writes the messages">
        {MESSAGE_WRITER_ORDER.map((value) => {
          const copy = MESSAGE_WRITER_COPY[value]
          const Icon = WRITER_ICONS[value]
          const disabled = value === 'mcp' && !mcpAvailable
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={writer === value}
              disabled={disabled}
              onClick={() => onWriterChange(value)}
              className={cn(
                'flex flex-col gap-1 rounded-lg border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                writer === value ? 'border-cobalt-bright bg-primary/10 ring-1 ring-cobalt-bright' : 'hover:bg-muted/50',
              )}
            >
              <span className="flex items-center gap-1.5 text-sm font-medium">
                <Icon className="h-4 w-4" />
                {copy.label}
                {writer === value && <Check aria-hidden className="ml-auto h-4 w-4 text-cobalt-bright" />}
              </span>
              <span className="text-xs text-muted-foreground">{copy.hint}</span>
            </button>
          )
        })}
      </div>
      {!mcpAvailable && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Bot className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {mcpLoading ? (
              'Checking your Claude connection...'
            ) : (
              <>
                Your Claude needs a Claude connection that can send messages.{' '}
                <button type="button" className="text-cobalt-bright underline" onClick={onOpenClaudeSettings}>
                  Set one up in Settings, Claude
                </button>
                . Your campaign setup is saved while you are there.
              </>
            )}
          </span>
        </p>
      )}
      {writer === 'mcp' && !mcpAvailable && !mcpLoading && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          This setup was saved with Your Claude, but there is no Claude connection that can send. Pick another
          method or set one up first.
        </p>
      )}
    </div>
  )
}

type StepMessagesProps = {
  readonly steps: readonly SequenceStep[]
  readonly onChange: (steps: readonly SequenceStep[]) => void
  readonly writer: WizardMessageWriter
  readonly templateAngle: TemplateChoice | null
  readonly onTemplateAngleChange: (angle: TemplateChoice) => void
  readonly templateSlots: Readonly<Record<string, string>>
  readonly onTemplateSlotsChange: (values: Readonly<Record<string, string>>) => void
  readonly campaignLink: string | null
  readonly fallbackHours: number
  readonly onFallbackHoursChange: (hours: number) => void
}

/** Step 4, Messages (plan N3): what to fill in depends on the method. */
function StepMessages({
  steps,
  onChange,
  writer,
  templateAngle,
  onTemplateAngleChange,
  templateSlots,
  onTemplateSlotsChange,
  campaignLink,
  fallbackHours,
  onFallbackHoursChange,
}: StepMessagesProps) {
  if (writer === 'template') {
    return (
      <div className="flex flex-col gap-3">
        <TemplateEditor
          steps={steps}
          onStepsChange={onChange}
          angle={templateAngle}
          onAngleChange={onTemplateAngleChange}
          slotValues={templateSlots}
          onSlotValuesChange={onTemplateSlotsChange}
          campaignLink={campaignLink}
        />
        <p className="text-sm font-semibold">Timing</p>
        {/* Timing only: no writing-sample editors or angle pins, the template is the message.
            Adding or removing a follow-up moves the unedited library text, so the break-up stays last. */}
        <SequenceBuilder
          steps={steps}
          onChange={(next) => onChange(templateAngle ? syncAngle(next, templateAngle) : next)}
          hideAngle
        />
      </div>
    )
  }
  if (writer === 'mcp') {
    return (
      <McpMessagesPanel
        steps={steps}
        onChange={onChange}
        fallbackHours={fallbackHours}
        onFallbackHoursChange={onFallbackHoursChange}
      />
    )
  }
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">
        Set the timing of each step. Writing samples are optional and set per step.
      </p>
      <SequenceBuilder steps={steps} onChange={onChange} showSamples />
    </div>
  )
}

/**
 * Messages for Your Claude: timing only (Claude writes the text), the
 * ready-made routine prompt from Settings > Claude with copy buttons, and
 * the hand-back to DealCloser AI. Connection requests are still sent by
 * DealCloser itself.
 */
function McpMessagesPanel({
  steps,
  onChange,
  fallbackHours,
  onFallbackHoursChange,
}: {
  readonly steps: readonly SequenceStep[]
  readonly onChange: (steps: readonly SequenceStep[]) => void
  readonly fallbackHours: number
  readonly onFallbackHoursChange: (hours: number) => void
}) {
  // Samples typed while "AI writes each message" was picked are kept: Claude
  // sees them through get_lead_brief, and DealCloser AI uses them when it
  // takes over. Say so, since this step does not show them.
  const keptSamples = steps.some((s) => (s.style_samples ?? []).some((x) => x.trim().length > 0))
  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-md border bg-muted/30 px-3 py-2.5 text-xs text-muted-foreground">
        Your Claude writes the first DMs, follow-ups and replies. DealCloser still sends the connection requests
        itself, and keeps the timing below, your daily limits and your sending hours.
        {keptSamples &&
          ' The writing samples you added under AI writes each message are kept: Claude sees them, and DealCloser AI uses them if it takes over.'}
      </div>

      <div className="flex flex-col gap-2 rounded-lg border p-3">
        <p className="text-sm font-semibold">The prompt for your Claude</p>
        <p className="text-xs text-muted-foreground">
          Paste this into a Claude routine that runs on a schedule, or send it yourself. Claude then works through
          the replies, check-ins and messages that are due. The review-first version puts everything Claude writes
          in Inbox, Review for you to send.
        </p>
        <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-2.5 font-mono text-[11px] leading-relaxed">
          {routinePrompt('send')}
        </pre>
        <div className="flex flex-wrap gap-2">
          <CopyButton text={routinePrompt('send')} label="Copy prompt" />
          <CopyButton text={routinePrompt('draft')} label="Copy review-first version" />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="wizard-mcp-fallback">Hand back to DealCloser AI</Label>
        <Select value={String(fallbackHours)} onValueChange={(v) => onFallbackHoursChange(Number(v))}>
          <SelectTrigger id="wizard-mcp-fallback" className="sm:w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FALLBACK_OPTIONS.map((o) => (
              <SelectItem key={o.hours} value={String(o.hours)}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          If Claude has not written a due message after this long, DealCloser AI writes it so leads are not left
          waiting. Off means DealCloser never steps in for this campaign.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-sm font-semibold">Timing</p>
        <SequenceBuilder steps={steps} onChange={onChange} hideAngle />
      </div>
    </div>
  )
}

/**
 * Preview step in template mode: the messages exactly as they will be saved,
 * slots filled. {variables} stay visible; they are filled per lead at send.
 */
function TemplateMessagesPreview({ steps }: { readonly steps: readonly SequenceStep[] }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">
        These are sent exactly as written. The variables in braces are filled with each lead&rsquo;s details.
      </p>
      {steps.filter(isTemplateStep).map((step, i) => (
        <div key={`${step.type}-${i}`} className="flex flex-col gap-1">
          <p className="text-xs font-medium">{STEP_TYPE_LABELS[step.type] ?? step.type}</p>
          <p className="whitespace-pre-wrap rounded-md bg-muted/40 px-3 py-2 text-sm leading-relaxed">
            {step.template?.trim() || 'No message yet.'}
          </p>
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// AI output preview (TJ's ask, 2026-07-14), shown on Preview and launch for
// "AI writes each message": what the AI's messages would look like, from the
// per-step writing samples, BEFORE the campaign exists or anything sends.
// Generated against one generic placeholder prospect using the SAME model
// the account's real sends use (2026-09-21), so the preview reflects the
// writing samples the user just pasted. Server caches by (steps, samples,
// offer, plan) for an hour — bouncing between steps doesn't regenerate.
// ---------------------------------------------------------------------------

type GenericPreviewStepResult = {
  readonly type: string
  readonly strategy: string
  readonly message: string | null
  readonly error?: string
  readonly no_body?: boolean
  /** Quality-gate verdict: what a live send would do with this draft. */
  readonly verdict?: PreviewVerdict
}

type GenericPreviewData = {
  readonly steps: readonly GenericPreviewStepResult[]
  readonly generated_at: string
  readonly cached: boolean
}


// ---------------------------------------------------------------------------
// Conversation simulator (owner request 2026-09-21): the message preview only
// shows outbound. This runs a whole thread against a REAL lead from the
// user's list, through the same reply code production uses, so they can see
// how the AI handles a push-back, an interested lead that books a call, and a
// flat no. Real calendar times, no booking, nothing sent.
// ---------------------------------------------------------------------------

type SimulationTurn = {
  readonly who: 'ai' | 'lead'
  readonly text: string
  readonly note?: string
  /** Quality-gate verdict on a message the AI wrote (absent on templates). */
  readonly verdict?: PreviewVerdict
}

type SimulationData = {
  readonly scenario: string
  readonly scenario_label: string
  readonly booking_used: boolean
  readonly lead: {
    readonly name: string
    readonly title: string | null
    readonly company: string | null
    readonly location: string | null
    readonly headline: string | null
    readonly linkedin_url: string | null
    readonly recent_posts: readonly string[]
  }
  readonly turns: readonly SimulationTurn[]
}

const SIM_SCENARIOS = [
  { id: 'objection', label: 'They push back' },
  { id: 'books_call', label: 'They book a call' },
  { id: 'not_interested', label: 'They say no' },
  { id: 'laid_off', label: 'They were laid off' },
  { id: 'between_roles_referral', label: 'Between roles, names someone' },
  { id: 'just_left', label: 'They just left the company' },
  { id: 'decision_elsewhere', label: 'Not their call to make' },
] as const

function ConversationSimulator({
  leadIds,
  styleSamples,
  ctaType,
  offerLink,
  brief,
}: {
  readonly leadIds: readonly string[]
  readonly styleSamples: readonly string[]
  readonly ctaType: CtaType
  readonly offerLink: string
  /** The brief being confirmed, so the simulation uses the real prompt. */
  readonly brief: CampaignBrief
}) {
  const apiFetch = useApi()
  const [scenario, setScenario] = useState<string | null>(null)
  const [data, setData] = useState<SimulationData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function run(next: string) {
    setScenario(next)
    setIsLoading(true)
    setError(null)
    setData(null)
    try {
      const res = await apiFetch<SimulationData>('/api/campaigns/preview-conversation', {
        method: 'POST',
        body: {
          scenario: next,
          lead_ids: leadIds.slice(0, 50),
          style_samples: styleSamples.filter((x) => x.trim().length > 0),
          cta_type: ctaType,
          ...(offerLink.trim() ? { offer_link: offerLink.trim() } : {}),
          campaign_brief: brief,
        },
      })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Simulation failed')
      setData(res.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Simulation failed')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="rounded-lg border p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium flex items-center gap-1.5">
            <MessagesSquare className="h-4 w-4 text-cobalt-bright" />
            See a full conversation
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Runs against a real lead from your list, using the same AI that answers your inbox.
            Real times from your calendar, no meeting booked, nothing sent.
          </p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {SIM_SCENARIOS.map((s) => (
          <Button
            key={s.id}
            size="sm"
            variant={scenario === s.id ? 'default' : 'outline'}
            className="h-7 text-xs"
            disabled={isLoading}
            onClick={() => void run(s.id)}
          >
            {isLoading && scenario === s.id && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
            {s.label}
          </Button>
        ))}
      </div>

      {error && <p className="mt-3 text-xs text-destructive">{error}</p>}
      {isLoading && !data && (
        <p className="mt-3 text-xs text-muted-foreground">
          Writing the conversation, this takes a few seconds per message.
        </p>
      )}

      {data && (
        <div className="mt-3 space-y-3">
          <div className="rounded-md border bg-muted/30 p-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">{data.lead.name}</span>
              {data.lead.linkedin_url && (
                <a
                  href={data.lead.linkedin_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-cobalt-bright underline underline-offset-2"
                >
                  LinkedIn profile
                </a>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {[data.lead.title, data.lead.company, data.lead.location].filter(Boolean).join(' · ')}
            </p>
            {data.lead.headline && (
              <p className="mt-1 text-xs text-muted-foreground italic">{data.lead.headline}</p>
            )}
            {data.lead.recent_posts.length > 0 && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                Recent post the AI can see: {data.lead.recent_posts[0]?.slice(0, 120)}
              </p>
            )}
          </div>

          {data.turns.map((turn, i) => (
            <div
              key={i}
              className={turn.who === 'ai' ? 'flex flex-col items-end' : 'flex flex-col items-start'}
            >
              <div
                className={[
                  'max-w-[85%] rounded-lg px-3 py-2 text-xs whitespace-pre-wrap',
                  turn.who === 'ai' ? 'bg-primary/10' : 'bg-muted',
                ].join(' ')}
              >
                <p className="mb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                  {turn.who === 'ai' ? 'Your AI' : data.lead.name}
                </p>
                {turn.text}
              </div>
              {turn.note && (
                <p className="mt-0.5 text-[10px] text-muted-foreground">{turn.note}</p>
              )}
              {turn.verdict && (
                <div className="mt-1 max-w-[85%]">
                  <PreviewVerdictBadge verdict={turn.verdict} />
                </div>
              )}
            </div>
          ))}
          <p className="text-[11px] text-muted-foreground">
            The lead&rsquo;s replies are a fixed script so you can compare scenarios. Everything the
            AI says here is written live, the same way it answers real replies, and passes the same
            quality check, shown under each message.
          </p>
        </div>
      )}
    </div>
  )
}

type StepAIPreviewProps = {
  /** Each step carries its own writing samples (style_samples_mode 'per_step'), like a real send. */
  readonly sequenceSteps: readonly SequenceStep[]
  /** Leads picked in step 2 — the simulator runs against one of them. */
  readonly leadIds: readonly string[]
  /** Campaign call to action from Step 1; shapes the ask in every preview. */
  readonly ctaType: CtaType
  /** Campaign-specific link from Step 1, previewed on the last step. */
  readonly offerLink: string
  /** The messaging brief from the What the AI knows step. */
  readonly brief: CampaignBrief
}

/** Non-empty writing samples of one step. */
function stepSamples(step: SequenceStep | undefined): string[] {
  return (step?.style_samples ?? []).filter((v) => v.trim().length > 0)
}

function StepAIPreview({ sequenceSteps, leadIds, ctaType, offerLink, brief }: StepAIPreviewProps) {
  const apiFetch = useApi()
  const [data, setData] = useState<GenericPreviewData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const firedRef = useRef(false)

  const anySamples = sequenceSteps.some((s) => stepSamples(s).length > 0)
  // The simulator's opener is a first DM, so it uses the First DM's samples.
  const firstDmSamples = stepSamples(sequenceSteps.find((s) => s.type === 'first_dm'))

  async function generatePreview(force: boolean) {
    setIsLoading(true)
    setError(null)
    try {
      const res = await apiFetch<GenericPreviewData>('/api/campaigns/preview-generic', {
        method: 'POST',
        body: {
          steps: sequenceSteps.map((s) => ({
            type: s.type,
            strategy: s.strategy,
            style_samples: stepSamples(s),
          })),
          style_samples: [],
          style_samples_mode: 'per_step',
          cta_type: ctaType,
          ...(offerLink.trim() ? { offer_link: offerLink.trim() } : {}),
          campaign_brief: brief,
          force,
        },
      })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Preview failed')
      setData(res.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview failed')
    } finally {
      setIsLoading(false)
    }
  }

  // Auto-generate once when the step mounts. Re-entering the step remounts
  // and re-fires, but the server's cache answers instantly for unchanged
  // inputs — and regenerates only when the user actually edited something.
  useEffect(() => {
    if (firedRef.current) return
    firedRef.current = true
    void generatePreview(false)
  }, []) // fire once on mount — firedRef guards StrictMode double-invoke

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium flex items-center gap-1.5">
            <Sparkles className="h-4 w-4 text-cobalt-bright" />
            What your outreach will look like
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Draft messages for a sample prospect (Alex Morgan, Operations Manager at Meridian
            Consulting), written by the same AI model your real sends use, from your sequence,
            offer, and writing samples. Nothing is sent.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={() => void generatePreview(true)}
          disabled={isLoading}
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
          Regenerate
        </Button>
      </div>

      {!anySamples && (
        <p className="rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
          No writing samples yet. They are optional: to make these drafts sound like you, go back to{' '}
          <span className="font-semibold">Messages</span> and paste up to 3 messages on a step.
        </p>
      )}

      {isLoading && !data && (
        <div className="flex flex-col items-center gap-2 py-10 text-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Drafting your messages…</p>
        </div>
      )}

      {error && !isLoading && (
        <div className="flex flex-col items-center gap-2 py-8 text-center">
          <p className="text-sm text-destructive">{error}</p>
          <Button type="button" variant="outline" size="sm" onClick={() => void generatePreview(false)}>
            Try again
          </Button>
        </div>
      )}

      {data && (
        <div className="flex flex-col gap-3">
          {data.steps.map((step, index) => (
            <div key={`${step.type}-${index}`} className="rounded-lg border p-3">
              <div className="mb-1.5 flex items-center gap-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-cobalt-bright">
                  {index + 1}
                </span>
                <span className="text-xs font-semibold">
                  {STEP_TYPE_LABELS[step.type] ?? step.type}
                </span>
              </div>
              {step.no_body ? (
                <p className="text-xs italic text-muted-foreground">
                  Plain connection invite, no message attached (deliberate: invites without notes
                  get accepted more).
                </p>
              ) : step.message ? (
                <>
                  <p className="whitespace-pre-wrap rounded-md bg-muted/40 px-3 py-2 text-sm leading-relaxed">
                    {step.message}
                  </p>
                  {step.verdict && (
                    <div className="mt-1.5">
                      <PreviewVerdictBadge verdict={step.verdict} />
                    </div>
                  )}
                </>
              ) : (
                <p className="text-xs text-destructive">
                  Could not draft this step{step.error ? `: ${step.error}` : ''}. Try Regenerate.
                </p>
              )}
            </div>
          ))}
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Each draft shows the quality check a live send gets: whether it would send, be held for
            your review, or be blocked. Nothing here is sent. Written by the same model your live
            sends use. Real outreach also personalizes to the
            specific lead (their role, company, and recent activity), so live messages carry more
            detail than this placeholder prospect allows.
          </p>
        </div>
      )}

      <ConversationSimulator
        leadIds={leadIds}
        styleSamples={firstDmSamples}
        ctaType={ctaType}
        offerLink={offerLink}
        brief={brief}
      />
    </div>
  )
}

/**
 * Preview for Your Claude (plan N7): no AI calls here, since DealCloser
 * would spend credits on text Claude will not send. It shows who writes
 * each step and the exact brief Claude reads through get_lead_brief.
 */
function McpPreview({ steps, brief }: { readonly steps: readonly SequenceStep[]; readonly brief: CampaignBrief }) {
  const rendered = renderCampaignBrief(brief)
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">
        Your Claude writes these when they are due, so there is nothing to draft here.
      </p>
      <div className="flex flex-col gap-1.5">
        {steps.map((step, index) => (
          <div key={`${step.type}-${index}`} className="flex items-start gap-2 rounded-md border px-3 py-2">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-cobalt-bright">
              {index + 1}
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-semibold">{STEP_TYPE_LABELS[step.type] ?? step.type}</span>
              <span className="block text-xs text-muted-foreground">
                {step.type === 'connection_request'
                  ? 'DealCloser sends a plain connection request, no note.'
                  : 'Your Claude writes this message.'}
              </span>
            </span>
          </div>
        ))}
        <p className="text-xs text-muted-foreground">Replies to leads are written by your Claude too.</p>
      </div>
      <div className="flex flex-col gap-1.5">
        <p className="text-xs font-medium">What your Claude reads for every lead (get_lead_brief)</p>
        <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-2.5 font-mono text-[11px] leading-relaxed">
          {rendered || 'Nothing yet. Go back to What the AI knows.'}
        </pre>
      </div>
    </div>
  )
}

type StepLaunchProps = {
  readonly state: WizardState
  readonly onActivateNowChange: (value: boolean) => void
  /** The account's offer, for the summary. */
  readonly offer: OfferConfig | null | undefined
  /** The steps as they will be saved (template slots filled). */
  readonly savedSteps: readonly SequenceStep[]
}

function SummaryRow({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="max-w-[60%] truncate text-right text-sm font-medium">{value}</span>
    </div>
  )
}

/**
 * Step 6, Preview and launch (plan N1): the summary that used to be the
 * Review step at the top, the preview for the chosen method (N7) in the
 * middle, and "Launch now or save as draft" at the bottom, next to Create.
 */
function StepLaunch({ state, onActivateNowChange, offer, savedSteps }: StepLaunchProps) {
  // Activation requires at least one lead — backend rejects with 400 otherwise.
  const canActivate = state.lead_ids.length > 0
  const extraLinks = cleanResourceRows(state.resources).length
  const fallback = FALLBACK_OPTIONS.find((o) => o.hours === state.mcp_fallback_hours)

  return (
    <div className="flex flex-col gap-5">
      <div className="rounded-lg border divide-y">
        <SummaryRow label="Campaign name" value={state.name || '(unnamed)'} />
        <SummaryRow label="Offer" value={offerOneLiner(offer) || '(none saved)'} />
        <SummaryRow label="Goal" value={CTA_TYPE_UI_LABELS[state.cta_type]} />
        <SummaryRow
          label="Link the AI sends"
          value={state.cta_type === 'direct_conversation' ? '(no link)' : state.offer_link.trim() || '(none)'}
        />
        {extraLinks > 0 && <SummaryRow label="Additional links" value={String(extraLinks)} />}
        <SummaryRow label="Leads" value={`${state.lead_ids.length} selected`} />
        <SummaryRow label="Method" value={MESSAGE_WRITER_COPY[state.message_writer].label} />
        {state.message_writer === 'mcp' && (
          <SummaryRow label="Hand back to DealCloser AI" value={fallback?.label ?? `After ${state.mcp_fallback_hours} hours`} />
        )}
        <SummaryRow label="Sequence" value={`${state.sequence_steps.length} steps`} />
        <SummaryRow
          label="Messaging brief"
          value={state.brief_confirmed ? 'Confirmed' : 'Not confirmed (go back to What the AI knows)'}
        />
        <SummaryRow label="Sending schedule" value="Weekdays, 9 AM to 5 PM ET" />
      </div>

      {state.message_writer === 'template' ? (
        <TemplateMessagesPreview steps={savedSteps} />
      ) : state.message_writer === 'mcp' ? (
        <McpPreview steps={state.sequence_steps} brief={state.brief} />
      ) : (
        <StepAIPreview
          sequenceSteps={state.sequence_steps}
          leadIds={state.lead_ids}
          ctaType={state.cta_type}
          offerLink={state.offer_link}
          brief={state.brief}
        />
      )}

      <div className="flex flex-col gap-2 rounded-lg border bg-muted/30 p-4">
        <p className="text-sm font-medium">Launch this campaign now?</p>
        <p className="text-xs text-muted-foreground">
          {canActivate
            ? 'If yes, outreach starts on the next scheduled run. If no, the campaign is saved as a draft you can launch later.'
            : 'Add at least one lead in the Leads step to launch right away.'}
        </p>
        <div className="flex gap-2 pt-1">
          <Button
            type="button"
            variant={state.activate_now ? 'outline' : 'default'}
            className="flex-1"
            onClick={() => onActivateNowChange(false)}
          >
            No, save as draft
          </Button>
          <Button
            type="button"
            variant={state.activate_now ? 'default' : 'outline'}
            className="flex-1"
            disabled={!canActivate}
            onClick={() => onActivateNowChange(true)}
          >
            Yes, launch now
          </Button>
        </div>
      </div>
    </div>
  )
}

/**
 * Step 5, What the AI knows (plan N5): a summary drafted from Settings >
 * Offer, FAQ and objections, the full interview behind "Edit details", and
 * the exact prompt with the confirm checkbox. Repo rule (meeting
 * 2026-09-18): the prompt is never hidden and every campaign confirms it,
 * whichever method writes the messages.
 */
function StepBrief({
  brief,
  confirmed,
  onBriefChange,
  onConfirmedChange,
  writer,
  offer,
  sources,
}: {
  readonly brief: CampaignBrief
  readonly confirmed: boolean
  /** Any edit un-confirms, so what is confirmed is always the final text. */
  readonly onBriefChange: (brief: CampaignBrief) => void
  readonly onConfirmedChange: (confirmed: boolean) => void
  readonly writer: WizardMessageWriter
  readonly offer: OfferConfig | null | undefined
  readonly sources: BriefSources
}) {
  const missing = missingBriefFields(brief)
  // Open the interview straight away when a required answer is missing.
  const [editing, setEditing] = useState(() => missing.length > 0)
  const editorRef = useRef<HTMLDivElement>(null)
  const rendered = renderCampaignBrief(brief)

  function openAt(field: string) {
    setEditing(true)
    setTimeout(() => {
      const input = document.getElementById(`brief-${field}`)
      if (input) input.focus()
      else editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 50)
  }

  const usedFor =
    writer === 'template'
      ? 'Your template messages are sent as written. DealCloser AI uses this when leads reply.'
      : writer === 'mcp'
        ? 'Your Claude reads this through get_lead_brief before it writes. DealCloser AI uses it too if it takes over.'
        : 'DealCloser AI uses this for every message and every reply in this campaign.'

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <p className="text-sm text-muted-foreground">
          Filled in from Settings, Offer, your FAQ and objections. Check it and add anything missing. {usedFor}
        </p>
      </div>

      <BriefSummary brief={brief} onEdit={openAt} />

      <div className="flex items-center justify-between gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => setEditing((e) => !e)} aria-expanded={editing}>
          {editing ? 'Hide details' : 'Edit details'}
        </Button>
      </div>

      {editing && (
        <div ref={editorRef}>
          <CampaignBriefEditor value={brief} onChange={onBriefChange} offer={offer} sources={sources} />
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <p className="text-sm font-semibold">The prompt the AI uses</p>
        <p className="text-xs text-muted-foreground">
          This is the exact text added to the AI&apos;s instructions for this campaign. DealCloser&apos;s core
          messaging rules (message structure, honesty, no AI tells) always apply on top of it.
        </p>
        <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-3 font-mono text-xs leading-relaxed">
          {rendered || 'Nothing yet. Answer the questions above and this fills in.'}
        </pre>
      </div>

      <label className="flex items-start gap-2 rounded-md border p-3 text-sm">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4"
          checked={confirmed}
          disabled={missing.length > 0}
          onChange={(e) => onConfirmedChange(e.target.checked)}
        />
        <span>
          I&apos;ve read this prompt. Use it for this campaign.
          <span className="block text-xs text-muted-foreground">
            {missing.length > 0
              ? 'Fill in the highlighted answers first.'
              : 'Required before the campaign can send. You can change it any time from the campaign page.'}
          </span>
        </span>
      </label>
    </div>
  )
}

type CreateCampaignWizardProps = {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly onCreated?: (id: string) => void
}

export function CreateCampaignWizard({
  open,
  onOpenChange,
  onCreated,
}: CreateCampaignWizardProps) {
  const [currentStep, setCurrentStep] = useState(1)
  const [state, setState] = useState<WizardState>(INITIAL_STATE)
  const [activationError, setActivationError] = useState<string | null>(null)
  const createCampaign = useCreateCampaign()
  const activateCampaign = useActivateCampaign()
  const apiFetch = useApi()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  // Prevent re-populating after the user has edited a field
  const autoPopulatedRef = useRef(false)
  // The brief is drafted from Settings once per open (see the effect below).
  const briefPrefilledRef = useRef(false)
  // Draft persistence (owner complaint, 2026-08-31): closing the wizard used
  // to throw away everything typed. Now the full form state is parked in
  // sessionStorage while the wizard is open and restored on the next open,
  // so both accidental closes and the deliberate "leave to add leads" detour
  // are lossless. `hydrated` gates the save effect so the very first render
  // of an open (still holding INITIAL_STATE) can't clobber a saved draft
  // before it has been read; the ref makes hydration one-shot per open.
  const [hydrated, setHydrated] = useState(false)
  const hydratedRef = useRef(false)
  // Whether this open restored a saved draft — drives the subtle
  // "resumed draft" notice with its Discard affordance.
  const [restoredFromDraft, setRestoredFromDraft] = useState(false)
  // Per-plan cap, mirrored from the lead picker: preselected ids arriving
  // from the Add-to-Campaign dialog must respect the same limit the picker
  // enforces on manual clicks.
  const limitsPlan = useLimitsPlan()
  const perCampaignCap = maxLeadsPerCampaignFor(limitsPlan)

  // On open: refresh the lead list (the user may return from an import with
  // brand-new rows the picker must show), then rehydrate any saved draft and
  // merge in preselected lead ids from the "create a campaign with these
  // leads" path.
  useEffect(() => {
    if (!open) {
      // Closing re-arms hydration for the next open. The draft itself stays
      // in sessionStorage, so an accidental close loses nothing.
      hydratedRef.current = false
      setHydrated(false)
      return
    }
    if (hydratedRef.current) return
    hydratedRef.current = true

    void queryClient.invalidateQueries({ queryKey: leadKeys.lists() })

    const draft = loadWizardDraft<Partial<WizardState>>()
    const preselect = takePreselectLeadIds()

    if (draft || preselect.length > 0) {
      setState((prev) => {
        const merged0: WizardState = draft ? { ...prev, ...draft.state } : prev
        // Drafts saved before the Messaging step (or by another build) may
        // carry no brief or an older shape.
        const base: WizardState = {
          ...merged0,
          brief: normalizeCampaignBrief(merged0.brief) ?? emptyCampaignBrief(),
          brief_confirmed: merged0.brief_confirmed === true,
          // A draft from another build must not carry a writer this one cannot show.
          message_writer: MESSAGE_WRITER_ORDER.includes(merged0.message_writer) ? merged0.message_writer : 'dca',
          mcp_fallback_hours: FALLBACK_OPTIONS.some((o) => o.hours === merged0.mcp_fallback_hours)
            ? merged0.mcp_fallback_hours
            : DEFAULT_FALLBACK_HOURS,
        }
        if (preselect.length === 0) return base
        let merged = [...new Set([...base.lead_ids, ...preselect])]
        if (Number.isFinite(perCampaignCap) && merged.length > perCampaignCap) {
          merged = merged.slice(0, perCampaignCap)
        }
        return { ...base, lead_ids: merged }
      })
      if (draft) {
        // Clamp defensively — a draft from a future/older build must not
        // strand the wizard on a step that doesn't exist.
        setCurrentStep(Math.min(Math.max(Math.trunc(draft.step), 1), TOTAL_STEPS))
        // Only announce the restore (and offer Discard) when the draft
        // actually differs from a fresh form — a pristine open-and-close
        // auto-saves INITIAL_STATE, and "continuing" an empty draft would
        // just be noise.
        const restoredState = { ...INITIAL_STATE, ...draft.state }
        const isPristine =
          draft.step === 1 &&
          JSON.stringify(restoredState) === JSON.stringify(INITIAL_STATE)
        setRestoredFromDraft(!isPristine)
      }
    }
    setHydrated(true)
  }, [open, queryClient, perCampaignCap])

  // Persist on every change while open. Gated on `hydrated` so this only
  // starts once the restore above has run (see comment there).
  useEffect(() => {
    if (!open || !hydrated) return
    saveWizardDraft(currentStep, state)
  }, [open, hydrated, currentStep, state])

  const { data: icpData } = useQuery({
    queryKey: ['settings', 'icp'],
    queryFn: async () => {
      const res = await apiFetch<IcpConfig>('/api/settings/icp')
      return res.success ? res.data : null
    },
    staleTime: 5 * 60_000,
  })

  const { data: offerData, isFetched: offerFetched } = useQuery({
    queryKey: ['settings', 'offer'],
    queryFn: async () => {
      const res = await apiFetch<OfferConfig>('/api/settings/offer')
      return res.success ? res.data : null
    },
    staleTime: 5 * 60_000,
  })

  // FAQ and objections (Settings > Offer) also draft the brief (plan N5).
  // Same query keys as the Settings cards, so their saves refresh these.
  const { data: faqData, isFetched: faqFetched } = useQuery({
    queryKey: ['settings', 'faq'],
    queryFn: async () => {
      const res = await apiFetch<FaqConfig>('/api/settings/faq')
      return res.success && res.data ? res.data : null
    },
    staleTime: 5 * 60_000,
  })

  const { data: objectionData, isFetched: objectionsFetched } = useQuery({
    queryKey: ['settings', 'objections'],
    queryFn: async () => {
      const res = await apiFetch<ObjectionConfig>('/api/settings/objections')
      return res.success && res.data ? res.data : null
    },
    staleTime: 5 * 60_000,
  })

  const briefSources: BriefSources = { faqs: faqData?.entries, objections: objectionData?.entries }

  // Your Claude needs a live connection that may write (Settings > Claude).
  const { data: mcpData, isLoading: mcpLoading } = useMcpConnections(open)
  const mcpAvailable = mcpData ? hasWriteConnection(mcpData.connections) : false
  const gateCtx = { mcpAvailable }

  // Auto-populate name and target_persona from settings when the wizard opens,
  // but only if the user hasn't typed anything yet.
  useEffect(() => {
    if (!open) return
    if (autoPopulatedRef.current) return
    if (!icpData && !offerData) return

    autoPopulatedRef.current = true

    setState((prev) => {
      // Build campaign name from ICP job titles or offer target audience
      let suggestedName = prev.name
      if (!suggestedName.trim()) {
        if (icpData?.jobTitles && icpData.jobTitles.length > 0) {
          suggestedName = `${icpData.jobTitles[0]} Outreach`
        } else if (offerData?.targetAudience) {
          suggestedName = `${offerData.targetAudience} Campaign`
        }
      }

      // Build target_persona from ICP
      let suggestedPersona = prev.target_persona
      if (!suggestedPersona.trim() && icpData) {
        const parts: string[] = []
        if (icpData.jobTitles.length > 0) parts.push(icpData.jobTitles.join(', '))
        if (icpData.industries.length > 0) parts.push(icpData.industries.join(', '))
        suggestedPersona = parts.join(' | ')
      }

      return { ...prev, name: suggestedName, target_persona: suggestedPersona }
    })
  }, [open, icpData, offerData])

  // Draft the brief once per open, after the restore above and once Offer,
  // FAQ and objections have all loaded (fills empty answers only). A brief
  // the user already confirmed is never touched: what they confirmed is
  // what gets saved.
  useEffect(() => {
    if (!open || !hydrated) return
    if (briefPrefilledRef.current) return
    if (!offerFetched || !faqFetched || !objectionsFetched) return
    briefPrefilledRef.current = true
    setState((prev) =>
      prev.brief_confirmed
        ? prev
        : { ...prev, brief: briefFromOffer(prev.brief, offerData, { faqs: faqData?.entries, objections: objectionData?.entries }) },
    )
  }, [open, hydrated, offerFetched, faqFetched, objectionsFetched, offerData, faqData, objectionData])

  function resetWizard() {
    setCurrentStep(1)
    setState(INITIAL_STATE)
    setActivationError(null)
    setRestoredFromDraft(false)
    autoPopulatedRef.current = false
    briefPrefilledRef.current = false
  }

  function handleClose(value: boolean) {
    // Closing (Cancel, Escape, overlay click) resets the local form but
    // deliberately KEEPS the sessionStorage draft: an accidental dismiss
    // must not lose work. The next open rehydrates from the draft; the only
    // ways to drop it are Discard draft and a successful create.
    if (!value) resetWizard()
    onOpenChange(value)
  }

  function handleDiscardDraft() {
    // Explicit abandon: drop the draft and any stale "come back to the
    // wizard" banner flag, then start over clean.
    clearWizardDraft()
    clearWizardReturn()
    resetWizard()
  }

  /**
   * Leave the wizard for a Settings page (Offer, Claude). Same detour as the
   * Leads step's "Find new leads": the draft is already saved, and the
   * return banner on Settings reopens the wizard where the user left off.
   */
  function goToSettings(path: string) {
    flagWizardReturn()
    navigate(path)
  }

  function canAdvance(): boolean {
    return stepReady(currentStep, state, gateCtx)
  }

  /**
   * The link the campaign saves (same rule as handleCreate). It fills
   * [your booking link]: the send only allows the campaign's own link.
   */
  const campaignLink = campaignLinkFor(state)
  /** Slot values as saved: typed slots plus [your booking link] from campaignLink. */
  const filledSlots = slotValuesWithLink(state.template_slots, campaignLink)
  function handleNext() {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep((s) => s + 1)
    }
  }

  function handleBack() {
    if (currentStep > 1) {
      setCurrentStep((s) => s - 1)
    }
  }

  async function handleCreate() {
    setActivationError(null)
    // Template mode saves the filled text per step plus the writer; AI mode
    // drops any template text (card S3); Your Claude saves the writer and
    // the hand-back hours.
    const writerPayload = messageWriterPayload(
      state.message_writer,
      state.sequence_steps,
      state.template_angle,
      filledSlots,
      state.mcp_fallback_hours,
    )
    const input: CreateCampaignInput = {
      name: state.name,
      daily_limit: state.daily_limit,
      lead_ids: state.lead_ids,
      sequence_config: {
        ...DEFAULT_SEQUENCE_CONFIG,
        steps: writerPayload.steps,
        daily_limit: state.daily_limit,
        // Writing samples are per step only (plan N4).
        style_samples_mode: 'per_step',
      },
      settings: {
        cta_type: state.cta_type,
        ...(state.cta_type !== 'direct_conversation' && state.offer_link.trim()
          ? { offer_link: state.offer_link.trim() }
          : {}),
        // Additional links & attachments — pruned/trimmed; omitted when none.
        ...(cleanResourceRows(state.resources).length > 0
          ? { resources: cleanResourceRows(state.resources) }
          : {}),
        // The confirmed messaging brief (meeting 2026-09-18). The server
        // stores it as version 1 and the campaign may send.
        brief: state.brief,
        brief_confirmed: state.brief_confirmed,
        ...writerPayload.settings,
      },
      target_persona: state.target_persona || null,
    }

    const result = await createCampaign.mutateAsync(input)

    if (state.activate_now && state.lead_ids.length > 0) {
      try {
        await activateCampaign.mutateAsync(result.id)
      } catch (err) {
        // Campaign was created as draft but activation failed (e.g. LinkedIn
        // not connected). Keep the dialog open with an inline error so the
        // user knows the campaign exists but is still in draft.
        setActivationError(
          err instanceof Error
            ? err.message
            : 'Could not activate campaign. It was saved as a draft. Activate it from the campaign page.',
        )
        return
      }
    }

    // The campaign exists — the draft has served its purpose. Clear it (and
    // any pending return-banner flag) so the next wizard open starts fresh.
    clearWizardDraft()
    clearWizardReturn()
    resetWizard()
    onOpenChange(false)
    onCreated?.(result.id)
  }

  // The brief and the three method cards need the wider dialog.
  const wide = currentStep === STEP.brief || currentStep === STEP.method

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={`${wide ? 'max-w-2xl' : 'max-w-xl'} max-h-[85vh] flex flex-col overflow-hidden`}>
        <DialogHeader>
          <DialogTitle>Create Campaign</DialogTitle>
          <DialogDescription>
            Set up a new outreach campaign in a few steps.
          </DialogDescription>
        </DialogHeader>

        {/* Progress */}
        <div className="border-b pb-4 shrink-0">
          <StepIndicator currentStep={currentStep} />
        </div>

        {/* Resumed-draft notice: closing the wizard never loses work, so tell
            the user they're continuing where they left off and give them the
            one explicit way out (a plain close keeps the draft). */}
        {restoredFromDraft && (
          <div className="flex items-center justify-between gap-2 shrink-0 rounded-md bg-muted/40 px-3 py-1.5">
            <p className="text-xs text-muted-foreground">
              Continuing your saved campaign setup.
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-muted-foreground"
              onClick={handleDiscardDraft}
            >
              Discard draft
            </Button>
          </div>
        )}

        {/* Step content */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {currentStep === STEP.details && (
            <Step1Details
              name={state.name}
              onChange={(name) => setState((s) => ({ ...s, name }))}
              ctaType={state.cta_type}
              onCtaTypeChange={(cta_type) => setState((s) => ({ ...s, cta_type }))}
              offerLink={state.offer_link}
              onOfferLinkChange={(offer_link) => setState((s) => ({ ...s, offer_link }))}
              settingsLink={offerData?.resourceLink}
              resources={state.resources}
              onResourcesChange={(resources) => setState((s) => ({ ...s, resources }))}
              offer={offerFetched ? offerData ?? null : undefined}
              onEditOffer={() => goToSettings(OFFER_SETTINGS_PATH)}
            />
          )}
          {currentStep === STEP.leads && (
            <Step2Leads
              selectedIds={state.lead_ids}
              onChange={(lead_ids) => setState((s) => ({ ...s, lead_ids }))}
            />
          )}
          {currentStep === STEP.method && (
            <StepMethod
              writer={state.message_writer}
              onWriterChange={(message_writer) =>
                setState((s) => ({
                  ...s,
                  message_writer,
                  // Steps may have changed in AI mode: put the break-up back on the last follow-up.
                  sequence_steps:
                    message_writer === 'template' && s.template_angle
                      ? syncAngle(s.sequence_steps, s.template_angle)
                      : s.sequence_steps,
                }))
              }
              mcpAvailable={mcpAvailable}
              mcpLoading={mcpLoading}
              onOpenClaudeSettings={() => goToSettings(CLAUDE_SETTINGS_PATH)}
            />
          )}
          {currentStep === STEP.messages && (
            <StepMessages
              steps={state.sequence_steps}
              onChange={(sequence_steps) => setState((s) => ({ ...s, sequence_steps }))}
              writer={state.message_writer}
              templateAngle={state.template_angle}
              onTemplateAngleChange={(template_angle) => setState((s) => ({ ...s, template_angle }))}
              templateSlots={state.template_slots}
              onTemplateSlotsChange={(template_slots) => setState((s) => ({ ...s, template_slots }))}
              campaignLink={campaignLink}
              fallbackHours={state.mcp_fallback_hours}
              onFallbackHoursChange={(mcp_fallback_hours) => setState((s) => ({ ...s, mcp_fallback_hours }))}
            />
          )}
          {currentStep === STEP.brief && (
            <StepBrief
              brief={state.brief}
              confirmed={state.brief_confirmed}
              onBriefChange={(brief) => setState((s) => ({ ...s, brief, brief_confirmed: false }))}
              onConfirmedChange={(brief_confirmed) => setState((s) => ({ ...s, brief_confirmed }))}
              writer={state.message_writer}
              offer={offerData}
              sources={briefSources}
            />
          )}
          {currentStep === STEP.launch && (
            <StepLaunch
              state={state}
              onActivateNowChange={(activate_now) =>
                setState((s) => ({ ...s, activate_now }))
              }
              offer={offerData}
              savedSteps={messageWriterPayload('template', state.sequence_steps, state.template_angle, filledSlots).steps}
            />
          )}
          {currentStep === STEP.launch && activationError && (
            <p className="mt-3 text-sm text-destructive">{activationError}</p>
          )}
        </div>

        <DialogFooter className="shrink-0 flex-row items-center gap-2 border-t pt-4 sm:gap-0">
          {currentStep > 1 && (
            <Button variant="outline" onClick={handleBack}>
              Back
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            className="mr-auto"
          >
            Cancel
          </Button>
          {currentStep < TOTAL_STEPS ? (
            <Button onClick={handleNext} disabled={!canAdvance()}>
              Next
            </Button>
          ) : (
            <Button
              onClick={handleCreate}
              disabled={
                createCampaign.isPending ||
                activateCampaign.isPending ||
                // A restored draft can land straight on this step, so every
                // earlier gate is checked again here: details, method, an
                // unfinished template and a confirmed brief.
                !canCreate(state, gateCtx)
              }
            >
              {createCampaign.isPending
                ? 'Creating...'
                : activateCampaign.isPending
                  ? 'Launching...'
                  : state.activate_now && state.lead_ids.length > 0
                    ? 'Create and launch'
                    : 'Create Campaign'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
